// Vocabulaire du domaine : source unique des valeurs énumérées.
// Le schéma de base (packages/db) et les validations (API, MCP) en dérivent.
// « En retard » n'est volontairement pas un statut : c'est un indicateur calculé.

export const TYPES_ENTITE = ['societe', 'activite_nom_propre', 'foyer'] as const
export type TypeEntite = (typeof TYPES_ENTITE)[number]

// Régimes fiscaux : les règles d'échéances (sprint 2) en dépendent.
export const REGIMES_TVA = [
  'reel_normal_mensuel',
  'reel_normal_trimestriel',
  'reel_simplifie',
  'franchise',
  'non_assujetti',
] as const
export type RegimeTva = (typeof REGIMES_TVA)[number]

export const REGIMES_IMPOT = ['is', 'ir', 'lmnp_reel', 'lmnp_micro'] as const
export type RegimeImpot = (typeof REGIMES_IMPOT)[number]

export const TYPES_PERSONNE = ['humain', 'assistant'] as const
export type TypePersonne = (typeof TYPES_PERSONNE)[number]

export const CANAUX_NOTIFICATION = ['slack', 'email', 'aucun'] as const
export type CanalNotification = (typeof CANAUX_NOTIFICATION)[number]

export const NIVEAUX_ROLE = ['administrateur', 'associe'] as const
export type NiveauRole = (typeof NIVEAUX_ROLE)[number]

/**
 * Accès temporaire d'un administrateur à un espace privé, accordé par sa titulaire : demandé,
 * accordé (lecture seule, jusqu'à sa fin), refusé, révoqué ou expiré.
 */
export const STATUTS_ACCES_TEMPORAIRE = [
  'demande',
  'accorde',
  'refuse',
  'revoque',
  'expire',
] as const
export type StatutAccesTemporaire = (typeof STATUTS_ACCES_TEMPORAIRE)[number]

export const ROLES_TIERS = [
  'cabinet',
  'fournisseur',
  'client',
  'locataire',
  'administration',
  'gestionnaire',
  /** Assurance maladie (CPAM, MSA…) ou mutuelle : rembourse les soins du foyer. */
  'organisme_sante',
  /** Personne privée hors de l'appli (l'autre parent d'un enfant) : jamais d'accès en V1. */
  'particulier',
] as const
export type RoleTiers = (typeof ROLES_TIERS)[number]

export const TYPES_CONTRAT = ['abonnement', 'pret', 'assurance', 'gestion'] as const
export type TypeContrat = (typeof TYPES_CONTRAT)[number]

export const FREQUENCES = [
  'mensuelle',
  'bimestrielle',
  'trimestrielle',
  'semestrielle',
  'annuelle',
  'ponctuelle',
] as const
export type Frequence = (typeof FREQUENCES)[number]

export const SOURCES_COMPTE = ['csv', 'dsp2'] as const
export type SourceCompte = (typeof SOURCES_COMPTE)[number]

export const CERTITUDES = ['fixe', 'fenetre', 'a_confirmer'] as const
export type Certitude = (typeof CERTITUDES)[number]

export const STATUTS_ECHEANCE = ['a_venir', 'en_preparation', 'faite', 'sans_objet'] as const
export type StatutEcheance = (typeof STATUTS_ECHEANCE)[number]

export const SENS_PAIEMENT = ['payer', 'encaisser'] as const
export type SensPaiement = (typeof SENS_PAIEMENT)[number]

// Moyens tels que la spécification les nomme pour les délais de grâce
// (prélèvement, virement émis, loyer, facture client), complétés des moyens courants.
export const MOYENS_PAIEMENT = [
  'prelevement',
  'virement_emis',
  'loyer',
  'facture_client',
  'carte',
  'cheque',
  'autre',
] as const
export type MoyenPaiement = (typeof MOYENS_PAIEMENT)[number]

export const STATUTS_PAIEMENT = [
  'prevu',
  'programme',
  'partiel',
  'paye',
  'rapproche',
  'rejete',
  'annule',
  'en_litige',
] as const
export type StatutPaiement = (typeof STATUTS_PAIEMENT)[number]

export const MODES_RAPPROCHEMENT = ['auto', 'manuel'] as const
export type ModeRapprochement = (typeof MODES_RAPPROCHEMENT)[number]

/**
 * Pourquoi deux lignes du relevé se compensent : paiement annulé ou remboursé par le même
 * marchand, débit en double remboursé, prélèvement rejeté, caution rendue, ou autre aller-retour.
 */
export const MOTIFS_COMPENSATION = [
  'annulation',
  'double_debit',
  'rejet',
  'caution',
  'aller_retour',
] as const
export type MotifCompensation = (typeof MOTIFS_COMPENSATION)[number]

export const TYPES_PIECE = [
  'facture',
  'avis',
  'courrier',
  'accuse_depot',
  'releve',
  'contrat',
  'fiche_paie',
  'autre',
] as const
export type TypePiece = (typeof TYPES_PIECE)[number]

/** « photo » : envoyée depuis l'appli installée sur le téléphone (photos ou PDF). */
/** « remplissage » : version remplie d'une autre pièce (formulaire), préparée par l'assistant. */
/** « portail » : relevée par le collecteur sur l'espace client d'un fournisseur. */
export const SOURCES_PIECE = [
  'gmail',
  'depot',
  'plateforme',
  'mcp',
  'photo',
  'remplissage',
  'portail',
] as const
export type SourcePiece = (typeof SOURCES_PIECE)[number]

/** D'où vient le texte intégral d'une pièce : couche texte du PDF, ou transcription du modèle. */
export const ORIGINES_TEXTE = ['couche_pdf', 'modele'] as const
export type OrigineTexte = (typeof ORIGINES_TEXTE)[number]

/**
 * Parcours d'une pièce : reçue, extraite, rattachée ; à vérifier quand un humain doit
 * trancher ; doublon d'une pièce déjà connue (même SIREN émetteur et même numéro) ; ignorée.
 */
export const STATUTS_PIECE = [
  'recue',
  'extraite',
  'rattachee',
  'a_verifier',
  'doublon',
  'ignoree',
  // Facture lue dans le fichier d'une autre pièce (`fichierPieceId`), qui porte le fichier, le
  // paiement, le rangement et le dépôt : aucun traitement des pièces rattachées ne la prend.
  'incluse',
] as const
export type StatutPiece = (typeof STATUTS_PIECE)[number]

/**
 * Envoi photo depuis le téléphone : en réception (pages attendues), complet, assemblé en une
 * pièce, ou en échec d'assemblage (à vérifier).
 */
export const STATUTS_ENVOI = ['reception', 'complet', 'assemble', 'echec'] as const
export type StatutEnvoi = (typeof STATUTS_ENVOI)[number]

/**
 * État d'un envoi montré dans « Mes envois » (calculé) ; « en attente d'envoi » n'existe que sur
 * le téléphone (file d'envoi locale).
 */
export const ETATS_ENVOI = [
  'recu',
  'en_lecture',
  'range',
  'classe',
  'a_verifier',
  'deja_recu',
  'ignore',
  'transmis',
] as const
export type EtatEnvoi = (typeof ETATS_ENVOI)[number]

/**
 * Transmission au cabinet (adresse de collecte Tiime) : à faire, faite, en attente (adresse de
 * collecte manquante), sans objet ; à décider (reçue dans une boîte personnelle : réglée par
 * la société, ou avancée par la personne ?) ; incertaine (envoi interrompu : arrivée ou non ?) ;
 * manuelle (fichier refusé par la collecte, à déposer à la main).
 */
export const TRANSMISSIONS_PIECE = [
  'a_faire',
  'faite',
  'en_attente',
  'sans_objet',
  'a_decider',
  'incertain',
  'manuelle',
] as const
export type TransmissionPiece = (typeof TRANSMISSIONS_PIECE)[number]

/** Rangement dans le Drive : à faire, en attente (chemin non renseigné), fait. */
export const RANGEMENTS_PIECE = ['a_faire', 'en_attente', 'fait'] as const
export type RangementPiece = (typeof RANGEMENTS_PIECE)[number]

/**
 * Version remplie d'un formulaire : brouillon préparé par l'assistant, puis validée par un
 * humain (N1), seulement alors rangée dans le Drive.
 */
export const REMPLISSAGES = ['brouillon', 'validee'] as const
export type Remplissage = (typeof REMPLISSAGES)[number]

/**
 * Anciens dossiers de rangement (chemins réglés à la main par entité) : servent tant que
 * l'entité n'a pas de racine de classement.
 */
export const RUBRIQUES_DRIVE = ['achats', 'ventes', 'releves', 'autres'] as const
export type RubriqueDrive = (typeof RUBRIQUES_DRIVE)[number]

/**
 * Rubrique du plan de classement, comme dans un dossier de révision : dossier permanent (ce
 * qui vaut d'un exercice à l'autre) et dossier de l'exercice, par cycle. Le foyer a ses
 * propres rubriques annuelles. Le plan (`packages/core/src/classement.ts`) dit où chacune se range.
 */
export const RUBRIQUES = [
  // Dossier permanent
  'juridique',
  'etat_civil',
  'contrats',
  'baux',
  'financements',
  'patrimoine',
  // Dossier de l'exercice (sociétés) ou de l'année (foyer)
  'ventes',
  'achats',
  'banque',
  'fiscal',
  'social',
  'travaux',
  'courriers',
  'cloture',
  'revenus',
  'sante',
  'famille',
  'logement',
] as const
export type Rubrique = (typeof RUBRIQUES)[number]

/**
 * Principe d'ordre d'un dossier du plan de classement : ce qu'il contient. Ses sous-dossiers
 * seulement ; les fichiers directement ; un dossier par exercice ou par année civile (nom
 * calculé, seule chose créée sans humain), qui contient ses sous-dossiers ou, sans eux, les
 * fichiers.
 */
export const ORDRES_DOSSIER = ['sous_dossiers', 'fichiers', 'par_exercice', 'par_annee'] as const
export type OrdreDossier = (typeof ORDRES_DOSSIER)[number]

/** Dossier du plan en service, ou proposé par l'assistant et pas encore validé par un humain. */
export const STATUTS_DOSSIER = ['actif', 'propose'] as const
export type StatutDossier = (typeof STATUTS_DOSSIER)[number]

/** Qui a réglé une facture reçue dans une boîte personnelle. */
export const REGLEMENTS_PIECE = ['societe', 'avance'] as const
export type ReglementPiece = (typeof REGLEMENTS_PIECE)[number]

/**
 * Où en est une pièce (calculé) : en lecture, à vérifier, à décider (humain), Drive ou
 * cabinet en échec, en attente (adresse ou chemin à renseigner), en cours, partiel (une
 * destination faite), terminé ; doublon, rejetée ; version remplie à valider par un humain.
 */
export const ETATS_ACHEMINEMENT = [
  'en_lecture',
  'a_verifier',
  'a_decider',
  'drive_echec',
  'tiime_echec',
  'en_attente',
  'en_cours',
  'partiel',
  'termine',
  'doublon',
  'rejetee',
  'a_valider',
] as const
export type EtatAcheminement = (typeof ETATS_ACHEMINEMENT)[number]

/**
 * Comment le cabinet reçoit les justificatifs : rien à envoyer, par mail à l'adresse de collecte
 * (Tiime), ou déposés chez le cabinet par l'agent du Mac (dépôt direct, `justificatifs.mode` du
 * fournisseur dans portails.ts) : dans Tiime, ou dans Dougs (décision du 12/10/2026). Les deux
 * voies sont exclusives : une entité en dépôt direct n'envoie jamais de mail.
 */
export const MODES_TRANSMISSION = ['aucune', 'mail', 'tiime', 'dougs'] as const
export type ModeTransmission = (typeof MODES_TRANSMISSION)[number]

/** Drive partagé où se range une entité. */
export const ESPACES_DRIVE = ['famille', 'flow_design'] as const
export type EspaceDrive = (typeof ESPACES_DRIVE)[number]

export const IMPOTS = ['cfe', 'taxe_fonciere', 'is', 'tva', 'ir', 'autre'] as const
export type Impot = (typeof IMPOTS)[number]

export const MODES_REGLEMENT = [
  'prelevement',
  'virement',
  'carte',
  'cheque',
  'deja_paye',
  'inconnu',
] as const
export type ModeReglement = (typeof MODES_REGLEMENT)[number]

/**
 * Classement d'un mail d'une boîte personnelle, sur ses seules métadonnées : demande
 * administrative, facture ou pièce annoncée, ou sans rapport (rien n'en est gardé).
 */
export const CLASSEMENTS_MAIL = ['demande_admin', 'facture_ou_piece', 'sans_rapport'] as const
export type ClassementMailCode = (typeof CLASSEMENTS_MAIL)[number]

/** Boîte personnelle : relevée, à reconnecter (autorisation retirée ou expirée), déconnectée. */
export const ETATS_BOITE = ['active', 'reconnexion', 'deconnectee'] as const
export type EtatBoite = (typeof ETATS_BOITE)[number]

/**
 * Recherche de la pièce d'une opération bancaire dans les mails anciens : paliers de requêtes
 * Gmail, du plus étroit au plus large. Chacun porte un terme distinctif (expéditeur, référence
 * ou mot) : jamais un montant seul. `nom_montant` : le nom du fournisseur que Claude a lu dans
 * le libellé (« NUAGE.IO » : Nuagerie), avec le montant, quand aucun expéditeur n'est connu
 * (décision du 14/10/2026).
 */
export const PALIERS_RECHERCHE = [
  'expediteur_montant',
  'reference',
  'expediteur_mots',
  'expediteur',
  'mots_montant',
  'nom_montant',
  'mots',
] as const
export type PalierRecherche = (typeof PALIERS_RECHERCHE)[number]

/**
 * Paliers forts : la référence exacte, ou l'expéditeur connu du fournisseur (seul, avec le montant
 * ou avec des mots). Un doute qu'ils rendent est proposé sur l'entité cherchée, à vérifier ; des
 * mots, seuls ou avec un montant, ne suffisent pas, ni le nom lu par Claude.
 */
export const PALIERS_FORTS = [
  'expediteur_montant',
  'reference',
  'expediteur_mots',
  'expediteur',
] as const satisfies readonly PalierRecherche[]

/**
 * Ce qui coupe une recherche, à reprendre plus tard : trop de mails, part du plafond IA, budget,
 * erreur de lecture d'une boîte (Gmail), erreur du classement (Claude) ou erreur interne.
 */
export const COUPURES_RECHERCHE = [
  'plafond_mails',
  'plafond_ia',
  'budget',
  'erreur_gmail',
  'erreur_ia',
  'erreur_interne',
] as const
export type CoupureRecherche = (typeof COUPURES_RECHERCHE)[number]

/**
 * Opérations bancaires sans pièce attendue : frais du compte, assurance et échéance d'un prêt,
 * taxe foncière mensualisée. On n'y cherche pas de facture.
 */
export const CATEGORIES_SANS_PIECE = [
  'frais_bancaires',
  'assurance_pret',
  'echeance_pret',
  'taxe_fonciere',
] as const
export type CategorieSansPiece = (typeof CATEGORIES_SANS_PIECE)[number]

/**
 * Issue d'un mail passé au tri d'une recherche (bilan, sans rien de son contenu) : gardé, gardé
 * avec sa pièce proposée sur l'entité cherchée (doute rendu par un palier fort), sans rapport,
 * laissé dans Gmail (demande, doute, sans PDF ni XML), reçu dans le domaine de la relève,
 * doublon d'un mail déjà vu, supprimé, ou déjà écrit par une relève concurrente.
 */
export const ISSUES_MAIL_RECHERCHE = [
  'garde',
  'propose',
  'sans_rapport',
  'laisse_demande',
  'laisse_doute',
  'laisse_autre_cloison',
  'laisse_sans_piece',
  'domaine_releve',
  'doublon',
  'supprime',
  'deja_connu',
] as const
export type IssueMailRecherche = (typeof ISSUES_MAIL_RECHERCHE)[number]

/**
 * Consigne de tri (mémoire de l'assistant) : proposée par l'assistant et à valider par un
 * humain, active, ou mise de côté. Seule une consigne active guide le tri.
 */
export const STATUTS_CONSIGNE = ['proposee', 'active', 'inactive'] as const
export type StatutConsigne = (typeof STATUTS_CONSIGNE)[number]

/**
 * Références lues sur une pièce, qui désignent un endroit ou un abonnement chez un fournisseur
 * à plusieurs endroits : PRM ou PDL (électricité), PCE (gaz), numéro de contrat, numéro de
 * client, numéro de ligne (télécom), adresse du point de livraison ou du bien concerné.
 */
export const TYPES_REFERENCE = ['prm', 'pce', 'contrat', 'client', 'ligne', 'adresse'] as const
export type TypeReference = (typeof TYPES_REFERENCE)[number]

/** Qui exécute la tâche née d'une consigne : les responsables, le gestionnaire du bail, un tiers. */
export const EXECUTANTS_CONSIGNE = ['responsables', 'gestionnaire', 'tiers'] as const
export type ExecutantConsigne = (typeof EXECUTANTS_CONSIGNE)[number]

/**
 * Condition d'une consigne sur l'objet du mail, sans modèle : les quatre premières
 * déclenchent (l'une suffit), « ne contient pas » écarte (toutes exigées).
 */
export const OPERATEURS_OBJET = [
  'est',
  'contient',
  'commence_par',
  'finit_par',
  'ne_contient_pas',
] as const
export type OperateurObjet = (typeof OPERATEURS_OBJET)[number]

/**
 * Boîte d'envoi (N1) : courriel, courrier postal, consigne pour un agent navigateur,
 * rendez-vous à poser dans un agenda Google.
 */
export const TYPES_BROUILLON = ['courriel', 'courrier', 'navigation', 'rendez_vous'] as const
export type TypeBrouillon = (typeof TYPES_BROUILLON)[number]

/**
 * Préparé, relu et validé, fait par un humain, abandonné. Rien ne part tout seul ; un
 * rendez-vous validé est posé dans l'agenda par l'appli et passe aussitôt à « fait ».
 */
export const STATUTS_BROUILLON = ['brouillon', 'valide', 'fait', 'abandonne'] as const
export type StatutBrouillon = (typeof STATUTS_BROUILLON)[number]

/**
 * Accès bancaire (agrégateur DSP2) : relevé, à reconnecter (consentement expiré ou retiré),
 * déconnecté.
 */
export const ETATS_CONSENTEMENT = ['actif', 'reconnexion', 'deconnecte'] as const
export type EtatConsentement = (typeof ETATS_CONSENTEMENT)[number]

/** Espace de la banque où se fait la validation : comptes de particulier ou professionnels. */
export const TYPES_ACCES_BANQUE = ['particulier', 'professionnel'] as const
export type TypeAccesBanque = (typeof TYPES_ACCES_BANQUE)[number]

// Collecte web : documents relevés sur les portails des fournisseurs, par un collecteur à part
// (navigateur sans modèle, adaptateurs écrits dans le code). Voir packages/core/src/portails.ts.

/**
 * Une collecte : en file, en cours, en attente d'un humain (code, validation), reprise après
 * sa réponse, terminée, en échec, bloquée (le serveur ne peut pas finir seul : clé d'accès,
 * CAPTCHA, identifiants refusés), expirée (personne n'a répondu à temps).
 */
export const STATUTS_COLLECTE = [
  'en_file',
  'en_cours',
  'attente_humain',
  'reprise',
  'terminee',
  'echec',
  'bloquee',
  'expiree',
  /** Retirée avant d'avoir abouti (connexion désactivée, fournisseur coupé, ordinateur délié). */
  'annulee',
] as const
export type StatutCollecte = (typeof STATUTS_COLLECTE)[number]

/**
 * Rythme auquel le fournisseur publie ses documents sur une connexion (décision du 03/10/2026) :
 * seul un rythme connu permet de repérer un mois manquant ; « à l'usage » (une facture par
 * course) et « inconnue » n'en repèrent aucun.
 */
export const CADENCES_CONNEXION = [
  'mensuelle',
  'bimestrielle',
  'trimestrielle',
  'semestrielle',
  'annuelle',
  'a_l_usage',
  'inconnue',
] as const
export type CadenceConnexion = (typeof CADENCES_CONNEXION)[number]

/** Sorte de document qu'un portail met à disposition (besoin d'une collecte, liste d'un adaptateur). */
export const TYPES_DOCUMENT_PORTAIL = ['facture', 'avis', 'releve', 'decompte', 'autre'] as const
export type TypeDocumentPortail = (typeof TYPES_DOCUMENT_PORTAIL)[number]

/**
 * Pourquoi une collecte n'a pas abouti. Les cinq premières sont une intervention humaine normale
 * (le fournisseur demande quelqu'un) ; les quatre suivantes, un adaptateur à revoir ; le reste,
 * l'environnement (réseau, politique, ordinateur éteint) ou l'inconnu.
 */
export const CLASSES_ERREUR_COLLECTE = [
  'auth_requise',
  'mfa_requis',
  'captcha_requis',
  'consentement_requis',
  'session_expiree',
  'selecteur_invalide',
  'portail_modifie',
  'document_introuvable',
  'telechargement_echoue',
  'reseau',
  'politique_reseau',
  'agent_indisponible',
  'inconnue',
] as const
export type ClasseErreurCollecte = (typeof CLASSES_ERREUR_COLLECTE)[number]

/** Qui a lancé la collecte : la planification (sans humain) ou une personne. */
export const DECLENCHEURS_COLLECTE = ['planifie', 'manuel'] as const
export type DeclencheurCollecte = (typeof DECLENCHEURS_COLLECTE)[number]

/**
 * État d'une connexion, tenu par le service : jamais collectée, à jour, vérification requise
 * (un humain doit répondre), en erreur (nouvel essai prévu), bloquée (plus d'essai seul).
 */
export const ETATS_CONNEXION_PORTAIL = [
  'jamais',
  'ok',
  'verification_requise',
  'erreur',
  'bloquee',
] as const
export type EtatConnexionPortail = (typeof ETATS_CONNEXION_PORTAIL)[number]

/** Ce que le fournisseur demande à un humain pendant la connexion. */
export const TYPES_DEFI = [
  'otp_sms',
  'otp_email',
  'totp',
  'push',
  'passkey',
  'webauthn',
  'connexion_interactive',
  'captcha',
  'changement_mot_de_passe',
  'consentement',
  'inconnu',
] as const
export type TypeDefi = (typeof TYPES_DEFI)[number]

export const STATUTS_DEFI = ['en_attente', 'resolu', 'expire', 'annule'] as const
export type StatutDefi = (typeof STATUTS_DEFI)[number]

/**
 * Demande de disponibilité (décision du 02/10/2026) : une connexion qui a besoin d'un humain ne se
 * relance qu'après que son titulaire a dit être là. Ouverte (en attente de sa réponse), acceptée
 * (les collectes repartent), reportée (« dans 1 h », « demain »), expirée (sans réponse), sans
 * objet (plus rien n'attend : une collecte relancée autrement, un défi expiré).
 */
export const STATUTS_DEMANDE_DISPONIBILITE = [
  'ouverte',
  'acceptee',
  'reportee',
  'expiree',
  'sans_objet',
] as const
export type StatutDemandeDisponibilite = (typeof STATUTS_DEMANDE_DISPONIBILITE)[number]

/** Réponses de report : « dans 1 h » (dans la plage), « demain » (à l'ouverture de la plage). */
export const REPORTS_DEMANDE = ['une_heure', 'demain'] as const
export type ReportDemande = (typeof REPORTS_DEMANDE)[number]

// Justificatifs sur un portail de cabinet (dépôt et liaison des pièces aux opérations bancaires
// lues chez le cabinet). Voir packages/core/src/justificatifs-portail.ts.

/**
 * Nature d'une opération bancaire : paiement par carte, prélèvement, virement, autre. Exactement
 * le type `NatureOperation` de operations-bancaires.ts (testé égal).
 */
export const NATURES_OPERATION = ['carte', 'prelevement', 'virement', 'autre'] as const

/**
 * État d'une opération lue sur un portail de justificatifs, d'après le plan du dernier passage :
 * sûre (sa pièce y sera déposée et reliée seule), proposée à un humain, à fournir (aucune pièce
 * en vue), déjà justifiée chez la source, reliée par l'appli, écartée par un humain, hors champ
 * (aucune pièce attendue), hors de la fenêtre de décision.
 */
export const ETATS_OPERATION_PORTAIL = [
  'sure',
  'proposee',
  'a_fournir',
  'justifiee',
  'reliee',
  'ecartee',
  'hors_champ',
  'hors_fenetre',
] as const
export type EtatOperationPortail = (typeof ETATS_OPERATION_PORTAIL)[number]

/**
 * Ligne du registre des justificatifs (une pièce, un document chez le portail, une opération) :
 * réservée pour un dépôt, déposée, reliée à son opération, incertaine (dépôt interrompu : arrivé
 * ou non ?), orpheline (déposée, jamais reliée), présente (déjà chez le cabinet), en échec,
 * annulée. Les six premières sont vivantes : jamais deux pour une même pièce.
 */
export const STATUTS_JUSTIFICATIF = [
  'reserve',
  'depose',
  'relie',
  'incertain',
  'orphelin',
  'present',
  'echec',
  'annule',
] as const
export type StatutJustificatif = (typeof STATUTS_JUSTIFICATIF)[number]

export const TYPES_TACHE = ['action', 'decision'] as const
export type TypeTache = (typeof TYPES_TACHE)[number]

export const ORIGINES_TACHE = ['generee', 'manuelle', 'mail', 'decision'] as const
export type OrigineTache = (typeof ORIGINES_TACHE)[number]

export const STATUTS_TACHE = [
  'a_faire',
  'en_cours',
  'en_attente_tiers',
  'faite',
  'annulee',
] as const
export type StatutTache = (typeof STATUTS_TACHE)[number]

/**
 * Ce que devient la tâche quand son envoi part (proposé par Claude, confirmé par l'humain) :
 * terminée, en attente de la réponse, ou rien.
 */
export const ISSUES_ENVOI = ['terminer', 'attendre_reponse', 'rien'] as const
export type IssueEnvoi = (typeof ISSUES_ENVOI)[number]

/** Projet : en cours, terminé ou abandonné (ses tâches gardent leur statut). */
export const STATUTS_PROJET = ['en_cours', 'termine', 'abandonne'] as const
export type StatutProjet = (typeof STATUTS_PROJET)[number]

// Niveaux d'autonomie : N0 automatique, N1 préparé puis validé, N2 humain seul.
// Aucun code de l'appli n'exécute de N2 ; le journal peut seulement enregistrer
// qu'un humain déclare l'avoir fait.
// Santé du foyer : soins des membres du foyer, remboursements de l'Assurance maladie, des
// mutuelles, et complément obtenu par l'autre parent d'un enfant auprès de sa mutuelle.

export const NATURES_SOIN = [
  'consultation',
  'dentaire',
  'orthodontie',
  'optique',
  'pharmacie',
  'analyses',
  'imagerie',
  'kinesitherapie',
  'hospitalisation',
  'autre',
] as const
export type NatureSoin = (typeof NATURES_SOIN)[number]

/**
 * Qui rembourse : le régime obligatoire (CPAM), une mutuelle du foyer, ou l'autre parent, qui
 * demande le complément à sa propre mutuelle avec les pièces qu'on lui transmet, puis le reverse.
 */
export const PAYEURS_SANTE = ['regime_obligatoire', 'mutuelle', 'autre_parent'] as const
export type PayeurSante = (typeof PAYEURS_SANTE)[number]

/** Remboursement d'un soin : attendu, demandé (dossier transmis), reçu, refusé, sans objet. */
export const STATUTS_REMBOURSEMENT = ['attendu', 'demande', 'recu', 'refuse', 'sans_objet'] as const
export type StatutRemboursement = (typeof STATUTS_REMBOURSEMENT)[number]

/** D'où vient un soin : saisi, repéré dans un mail, dans un agenda, ou lu sur une pièce. */
export const ORIGINES_SOIN = ['manuelle', 'mail', 'agenda', 'piece', 'portail'] as const
export type OrigineSoin = (typeof ORIGINES_SOIN)[number]

/**
 * D'où vient une ligne de frais à refacturer : posée à la main, par la règle du dossier du plan
 * où la pièce est classée, ou par une avance (facture d'une société réglée par une personne).
 */
export const ORIGINES_REFACTURATION = ['manuelle', 'dossier', 'avance'] as const
export type OrigineRefacturation = (typeof ORIGINES_REFACTURATION)[number]

/**
 * Qui a mis une pièce dans son dossier du plan : un humain (sur la fiche, ou l'assistant à sa
 * demande), Claude au placement, ou la règle d'un dossier qui suit sa ligne à refacturer.
 */
export const ORIGINES_CLASSEMENT = ['humain', 'placement', 'refacturation'] as const
export type OrigineClassement = (typeof ORIGINES_CLASSEMENT)[number]

/**
 * Réseau d'une carte de paiement lue sur un portail (avec ses quatre derniers chiffres, jamais
 * le numéro entier) : ce qui la distingue dans le tableau des cartes.
 */
export const RESEAUX_CARTE = ['visa', 'mastercard', 'amex', 'cb', 'maestro', 'autre'] as const
export type ReseauCarte = (typeof RESEAUX_CARTE)[number]

/** Ce qu'une pièce est pour un soin. */
export const ROLES_PIECE_SOIN = ['facture', 'ordonnance', 'decompte', 'devis', 'autre'] as const
export type RolePieceSoin = (typeof ROLES_PIECE_SOIN)[number]

export const NIVEAUX_AUTONOMIE = ['n0', 'n1', 'n2'] as const
export type NiveauAutonomie = (typeof NIVEAUX_AUTONOMIE)[number]

export const DEVISE_PAR_DEFAUT = 'EUR'
export const FUSEAU = 'Europe/Paris'
